import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import { useMismatchWarningRules } from '@/components/ReportForm/hooks/useMismatchWarningRules';
import { InputBase, LabelProps } from '@/components/ReportForm/InputBase';
import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import {
  isNotEqualTPM,
  useBusinessPersonData,
  useInvoiceRegistrationNumberAndSetBusinessPersonDataHooks,
  useTpmDataFromBusinessPersonData,
} from '@/context/BusinessPersonData';
import { TPMData } from '@/context/services/tpm/type';
import { useTpmInvoiceLoading } from '@/context/TpmInvoiceLoading';
import { useTranslation } from '@/i18n';
import {
  FormInputInvoiceRegistrationNumber,
  FormInputInvoiceRegistrationNumberProps,
  RegistrationBusinessPersonDataPopover,
} from '@moneyforward/ap-frontend-components';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  isDisabled: boolean;
} & InputBaseType<TFieldValues> &
  Omit<
    FormInputInvoiceRegistrationNumberProps<TFieldValues>,
    'businessPersonData' | 'callbackOnInput13Text' | 'captionRender'
  > & {
    callbackUpdateInvoiceRegistrationNumber: (
      tpmValue?: TPMData
    ) => Promise<void>;
    mismatchMessage?: string;
    // `null` opts out of the mismatch captionRender below and falls back to
    // the library's own default caption; `undefined` (the default) keeps it.
    captionRender?:
      | FormInputInvoiceRegistrationNumberProps<TFieldValues>['captionRender']
      | null;
  };

const InnerApReportFormInputInvoiceRegistrationNumberField = <
  TFieldValues extends FieldValues
>({
  control,
  name,
  inputId,
  label,
  tooltip,
  hidden,
  caption,
  isDisabled,
  callbackUpdateInvoiceRegistrationNumber,
  onChangeAfter,
  required = false,
  mismatchMessage,
  captionRender: callerCaptionRender,
  ...rest
}: Props<TFieldValues>) => {
  const businessPersonData = useBusinessPersonData();
  const tpm = useTpmDataFromBusinessPersonData();
  const payee = useSelectedApPayee();
  const { getFieldState, getValues, trigger } = useFormContext<TFieldValues>();
  const [onChangeFlag, setOnChangeFlag] = useState(false);
  const tpmRef = useRef<TPMData>(tpm);
  const payeeRef = useRef(payee);

  useEffect(() => {
    // 値を変更した時にも変更がかかる様に改修
    // Modify so that changes are applied when values are changed
    // 画面描画時に更新されてしまうことを防ぐ
    // Prevent updates from occurring during screen redrawing
    const { isDirty } = getFieldState(name);
    if ((isDirty || onChangeFlag) && isNotEqualTPM(tpmRef.current, tpm)) {
      callbackUpdateInvoiceRegistrationNumber(tpm);
      tpmRef.current = tpm;
      setOnChangeFlag(false);
    }
  }, [
    callbackUpdateInvoiceRegistrationNumber,
    getFieldState,
    name,
    onChangeFlag,
    tpm,
  ]);

  // Track payee changes and trigger revalidation
  useEffect(() => {
    if (payeeRef.current?.id !== payee?.id) {
      payeeRef.current = payee;
      trigger(name);
    }
  }, [payee, getValues, name, trigger]);

  const onWrapChangeAfter = useCallback(
    (value: string) => {
      setOnChangeFlag(true);
      onChangeAfter?.(value);
    },
    [onChangeAfter]
  );
  const { setTpmFetching } = useTpmInvoiceLoading();
  const {
    callBackAfterInput13TextWarningType,
    updateContextBusinessPersonData,
    isLoading,
  } = useInvoiceRegistrationNumberAndSetBusinessPersonDataHooks();
  setTpmFetching(isLoading);

  const { t } = useTranslation();

  const checkIsMismatchedNumberError = useCallback(
    (value: string | undefined, currentPayee = payee) => {
      if (!value || value.length !== 13) {
        return true;
      }

      const isSameNumber =
        currentPayee?.invoice_registration_number &&
        currentPayee.invoice_registration_number === value;

      if (currentPayee?.invoice_registration_number && !isSameNumber) {
        return t('registered_invoice_registration_number_does_not_match');
      }

      return true;
    },
    [payee, t]
  );

  const callBackAfterInput13text = useCallback(
    async (value: string | undefined) => {
      const result = await callBackAfterInput13TextWarningType(value);
      updateContextBusinessPersonData();

      // 1. 空白は必須ではない。OCR mismatch があればそれを出す。
      // Empty is allowed. Show the OCR mismatch when present.
      if (!value) {
        return mismatchMessage ?? true;
      }

      // 1桁〜12桁 / 14桁以上はライブラリの minLength / maxLength。警告は出さない。
      // Incomplete or too-long values: skip warnings; minLength / maxLength handles it.
      if (value.length !== 13) {
        return true;
      }

      // 2. 国税庁 API
      // National Tax Agency API
      if (result !== true) {
        return result;
      }

      // 3. 警告は1つ。OCR mismatch があればそれ（支払先不一致の代わり）。
      // Show only one warning. Prefer the OCR mismatch over the registered payee number mismatch.
      if (mismatchMessage) {
        return mismatchMessage;
      }

      return checkIsMismatchedNumberError(value);
    },
    [
      callBackAfterInput13TextWarningType,
      updateContextBusinessPersonData,
      checkIsMismatchedNumberError,
      mismatchMessage,
    ]
  );

  const labelProps: LabelProps = useMemo(
    () => ({ required, children: label, tooltip }),
    [required, label, tooltip]
  );
  const inputProps = useMemo(
    () => ({
      disabled: isDisabled,
      placeholder: t('enter_13_digit_number'),
    }),
    [isDisabled, t]
  );
  const { rules, captionRender } = useMismatchWarningRules<TFieldValues>(
    name,
    required,
    t('required'),
    mismatchMessage,
    callerCaptionRender
  );

  const businessPersonDataProps = useMemo(() => {
    return {
      ...businessPersonData,
    };
  }, [businessPersonData]);

  return (
    <InputBase labelProps={labelProps} description={caption} hidden={hidden}>
      {/* (1) 13文字エラーはライブラリの入力コンポーネント側 */}
      {/* The 13-character error is handled by the library input component. */}
      <FormInputInvoiceRegistrationNumber<TFieldValues>
        control={control}
        name={name}
        inputId={inputId}
        withPopover
        businessPersonData={businessPersonDataProps}
        callbackOnInput13Text={callBackAfterInput13text}
        inputProps={inputProps}
        rules={rules}
        onChangeAfter={onWrapChangeAfter}
        {...rest}
        captionRender={captionRender}
      />
    </InputBase>
  );
};

InnerApReportFormInputInvoiceRegistrationNumberField.displayName =
  'InnerApReportFormInputInvoiceRegistrationNumberField';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApReportFormInputInvoiceRegistrationNumberField = memo((props) => (
  <InnerApReportFormInputInvoiceRegistrationNumberField {...props} />
)) as typeof InnerApReportFormInputInvoiceRegistrationNumberField;
ApReportFormInputInvoiceRegistrationNumberField.displayName =
  'ApReportFormInputInvoiceRegistrationNumberField';

const WrapRegistrationBusinessPersonDataPopover = memo(() => {
  const businessPersonData = useBusinessPersonData();
  return <RegistrationBusinessPersonDataPopover {...businessPersonData} />;
});
WrapRegistrationBusinessPersonDataPopover.displayName =
  'WrapRegistrationBusinessPersonDataPopover';
